import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { analyticsEvent, getProductShell } from "../util/analytics";
import {
    ActiveTimeContext,
    ForegroundActiveTimeTracker,
    resolveActiveTimeFeature,
    resolveRouteViewerContext,
} from "../util/activeTimeTracker";
import { AnalyticsEvents } from "../util/eventRegistry";
import { productEventQueue } from "../util/productEventQueue";
import { useStytchMember, useStytchOrganization } from "./stytch-compat";
import type { ViewerContext } from "./useViewerOpenAnalytics";

const INPUT_EVENT_TYPES = ["pointerdown", "keydown", "wheel", "touchstart"] as const;
const INPUT_LISTENER_OPTIONS = { capture: true, passive: true } as const;

function createUsageSessionId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `usage-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const activeTimeTracker = new ForegroundActiveTimeTracker({
    emit: (slice) => analyticsEvent(AnalyticsEvents.ACTIVE_TIME_SLICE, { ...slice }),
    flushQueue: (keepalive) => productEventQueue.flushAll(keepalive),
    createUsageSessionId,
});

function useRouteActiveTimeContext(): ActiveTimeContext {
    const location = useLocation();
    const feature = resolveActiveTimeFeature(location.pathname);
    const viewerContext = resolveRouteViewerContext(
        location.pathname,
        location.search,
        location.state,
    );
    const shell = getProductShell();

    return useMemo(
        () => ({
            feature,
            path: location.pathname,
            shell,
            ...(viewerContext ? { viewerContext } : {}),
        }),
        [feature, location.pathname, shell, viewerContext],
    );
}

/** Counts the member's active time on this page. `countsHere` is false on a host that counts
 *  it itself (`Platform.activeTimeCounter`): the page then never starts, so a second is never
 *  counted by both. */
export function useActiveTimeTracking(countsHere = true): void {
    const context = useRouteActiveTimeContext();
    const contextRef = useRef(context);
    const { member } = useStytchMember();
    const { organization } = useStytchOrganization();
    const orgSlug = organization?.organization_slug ?? null;
    const isTracking = countsHere && member !== null && orgSlug !== null;
    contextRef.current = context;

    useEffect(() => {
        if (isTracking) activeTimeTracker.setRouteContext(context);
    }, [context, isTracking]);

    useEffect(() => {
        if (!isTracking) {
            activeTimeTracker.dispose();
            return;
        }

        activeTimeTracker.start(contextRef.current, {
            focused: document.hasFocus(),
            visible: document.visibilityState === "visible",
        });

        const handleInput = (event: Event) => activeTimeTracker.noteUserInput(event.isTrusted);
        const handleFocus = () => activeTimeTracker.setFocused(true);
        const handleBlur = () => activeTimeTracker.setFocused(false);
        const handleVisibility = () =>
            activeTimeTracker.setVisible(document.visibilityState === "visible");
        const handlePageHide = () => activeTimeTracker.setVisible(false);

        for (const type of INPUT_EVENT_TYPES) {
            document.addEventListener(type, handleInput, INPUT_LISTENER_OPTIONS);
        }
        window.addEventListener("focus", handleFocus);
        window.addEventListener("blur", handleBlur);
        window.addEventListener("pagehide", handlePageHide);
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            for (const type of INPUT_EVENT_TYPES) {
                document.removeEventListener(type, handleInput, INPUT_LISTENER_OPTIONS);
            }
            window.removeEventListener("focus", handleFocus);
            window.removeEventListener("blur", handleBlur);
            window.removeEventListener("pagehide", handlePageHide);
            document.removeEventListener("visibilitychange", handleVisibility);
            activeTimeTracker.dispose();
        };
    }, [isTracking, orgSlug]);
}

export function useViewerActiveTimeTracking(viewerContext?: ViewerContext): void {
    const tokenRef = useRef(Symbol("active-time-viewer"));

    useEffect(() => {
        if (viewerContext) {
            activeTimeTracker.setViewerOverride(tokenRef.current, viewerContext);
        } else {
            activeTimeTracker.clearViewerOverride(tokenRef.current);
        }
    }, [viewerContext]);

    useEffect(
        () => () => {
            activeTimeTracker.clearViewerOverride(tokenRef.current);
        },
        [],
    );
}
